import { isDefined } from "@clipboard-health/util-ts";
// We're importing MUI button for now rather than using the ui-components one
// eslint-disable-next-line no-restricted-imports
import { Box, Button, Stack } from "@mui/material";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { useSearchParams } from "@src/appV2/lib/utils/useSearchParams";
import { useSetSearchParams } from "@src/appV2/lib/utils/useSetSearchParams";
import { usePaginatedHiringFeed } from "@src/appV2/redesign/HiringFeed/api/usePaginatedHiringFeed";
import { useGetPlacementFilters } from "@src/appV2/redesign/Placements/components/PlacementDetailsPage/hooks/useGetPlacementFilters";
import { type Worker } from "@src/appV2/Worker/api/types";
import { USER_EVENTS } from "@src/constants";
import { isEmpty } from "lodash";
import { useEffect } from "react";

import { useLocalStorage } from "../../lib/utils";
import {
  defaultAgeGroups,
  defaultDaysOfWeek,
  defaultJobTypes,
  defaultShiftTypes,
} from "../PlacementCandidate/constants";
import { usePlacementFilterColdStart } from "../PlacementCandidate/hooks/usePlacementFilterColdStart";
import { useTrackMarketingLinkClicks } from "../PlacementCandidate/hooks/useTrackMarketingLinkClicks";
import { type PlacementCandidate } from "../PlacementCandidate/types";
import { getSafeAreaPaddings } from "../utils/getSafeAreaPaddings";
import { PlacementMapView } from "./components/PlacementMapView";
import { PlacementsListContainer } from "./components/PlacementsListContainer";
import { useBookingOrderingCohort } from "./hooks/useBookingOrderingCohort";
import { useGetPendingJobFairInviteAndShiftBookedData } from "./hooks/useGetPendingJobFairInviteAndShiftBookedData";
import { useIsJobSearchAgreementEnabled } from "./hooks/useIsJobSearchAgreementEnabled";
import { JobsMarketLaunchCountdownPage } from "./JobsMarketLaunchCountdownPage";
import { usePlacementsFilter } from "./usePlacementsFilter";
import { showApplicationsStorageKey } from "./utils";

interface PlacementsPageProps {
  placementCandidate: PlacementCandidate;
  worker: Worker;
  allWorkerQualificationsString: string;
  workerTypesAvailableForHiring?: string[];
}

/*
 TODO: Right now to make the bottom filters properly positioned, it's almost impossible due to a few factors:
 - This view renders inside old UI IonPage (which makes fixed positioning not account for the navbar due to how IonPage works)
 - It also renders inside the new UI which has a different layout - fixed positioning won't work properly without knowing the navbar height
 - It uses `PageWithHeader` wrapper which doesn't set a height to the page container, preventing us from using 100% height + absolute positioning for bottom filters
 - Navbar height is not hardcoded value, we only store navbar item height in the theme
 To fix this we need to:
 1. Remove the `PageWithHeader` wrapper (will happen during full transition to new UI)
 2. Make the buttons absolutely positioned in a container with height: calc(100% - navbar height)
    This will work automatically like it does in shift discovery, with different page structure
  As a temporary solution we will just hardcode the height of the navbar here (without the bottom padding) and use safe area padding
  with the same bottom value as the bottom padding of the buttons
*/

const NAVBAR_HEIGHT = 64;

export function PlacementsPage(props: PlacementsPageProps) {
  const {
    placementCandidate,
    worker,
    allWorkerQualificationsString,
    workerTypesAvailableForHiring,
  } = props;
  const [viewMode, setViewMode] = useLocalStorage<"list" | "map">("jobs-view-mode", "list");
  const [filter, setFilter] = usePlacementsFilter();
  const [showApplications, setShowApplications] = useLocalStorage<boolean>(
    showApplicationsStorageKey,
    false
  );

  const queryParameters = useSearchParams<{ showApplications?: string }>();
  const setSearchParams = useSetSearchParams();

  const marketLaunchFlagValue = useCbhFlag(CbhFeatureFlag.JOBS_NEW_MARKET_LAUNCH, {
    defaultValue: {
      enabled: false,
    },
  });

  const { hasPendingJobFairInvite, hasJobFairShift } = useGetPendingJobFairInviteAndShiftBookedData(
    {
      workerId: worker.userId,
      jobFairHcfId: marketLaunchFlagValue.enabled ? marketLaunchFlagValue.jobFairHcfId : "",
      jobFairTimestamp: marketLaunchFlagValue.enabled ? marketLaunchFlagValue.jobFairTimestamp : "",
      enabled: marketLaunchFlagValue.enabled,
    }
  );

  let shouldShowCountdown = false;

  if (marketLaunchFlagValue.enabled) {
    const currentTime = new Date();
    const marketLaunchTime = new Date(marketLaunchFlagValue.marketLaunchTimestamp);

    shouldShowCountdown = currentTime < marketLaunchTime;
  }

  // Cold start hook - reset filters on first app launch
  // Placed at the highest level to ensure it runs before any child components mount
  usePlacementFilterColdStart(
    () => {
      const filtersToSubmit = {
        distanceInMiles: undefined,
        jobTypes: defaultJobTypes,
        shiftTypes: defaultShiftTypes,
        workerTypes: allWorkerQualificationsString,
        daysOfWeek: defaultDaysOfWeek.replace(/\|/g, ","),
        ageGroups: defaultAgeGroups,
      };
      logEvent(APP_V2_APP_EVENTS.PLACEMENTS_PAGE_ACTIONS, {
        placementCandidateId: placementCandidate.id,
        action: "filter_reset_cold_start",
        filters: filtersToSubmit,
      });
      setFilter(filtersToSubmit);
    },
    isDefined(allWorkerQualificationsString) && !isEmpty(allWorkerQualificationsString)
  );

  useEffect(() => {
    const shouldShowApplicationsTab = queryParameters.showApplications; // ?showApplications=true
    if (isDefined(shouldShowApplicationsTab)) {
      const value = shouldShowApplicationsTab === "true";
      setShowApplications(value);

      setSearchParams({ showApplications: undefined }, { shouldReplace: true });
    }
  }, [queryParameters, setShowApplications, setSearchParams]);

  useTrackMarketingLinkClicks({
    placementCandidate,
    enabled: isDefined(placementCandidate),
  });

  const bookingReorderingCohort = useBookingOrderingCohort();
  const isJobSearchAgreementEnabled = useIsJobSearchAgreementEnabled();

  useLogEffect(USER_EVENTS.VIEWED_JOBS, {
    viewMode,
    filter: { ...filter, distanceInMiles: filter?.distanceInMiles?.$lt },
    showApplications,
    bookingReorderingCohort,
    isJobSearchAgreementEnabled,
  });

  useEffect(() => {
    return () => {
      // reset showApplications to false when next url does not have /placements/ in it
      if (!globalThis.location.pathname.includes("/placements/")) {
        setShowApplications(false);
      }
    };
  }, [setShowApplications]);

  const isListViewMode = viewMode === "list";

  const placementFilters = useGetPlacementFilters({
    filter,
    showApplications,
    worker,
    enabled: isDefined(placementCandidate),
  });

  const hiringFeedResults = usePaginatedHiringFeed(
    {
      workerId: worker.userId,
      placementCandidateId: placementCandidate.id,
      filter: placementFilters,
      workerTypesAvailableForHiring,
      bookingReorderingCohort,
    },
    {
      enabled: isDefined(worker.userId) && !showApplications && !isEmpty(filter),
    }
  );

  if (marketLaunchFlagValue.enabled && shouldShowCountdown) {
    return (
      <JobsMarketLaunchCountdownPage
        hasPendingJobFairInvite={hasPendingJobFairInvite}
        jobFairHcfId={marketLaunchFlagValue.jobFairHcfId}
        jobFairTimestamp={marketLaunchFlagValue.jobFairTimestamp}
        jobFairMsa={marketLaunchFlagValue.jobFairMsa}
        marketLaunchTimestamp={marketLaunchFlagValue.marketLaunchTimestamp}
        hasJobFairShift={hasJobFairShift}
      />
    );
  }

  return (
    <>
      {isListViewMode ? (
        <Box
          id="jobs-list-container"
          sx={{
            flex: 1,
            overflow: "auto",
            backgroundColor: (theme) => theme.palette.background.tertiary,
          }}
        >
          <PlacementsListContainer
            placementCandidate={placementCandidate}
            worker={worker}
            filter={filter}
            setFilter={setFilter}
            showApplications={showApplications}
            setShowApplications={setShowApplications}
            hiringFeedResults={hiringFeedResults}
          />
        </Box>
      ) : (
        <PlacementMapView
          worker={worker}
          filter={filter}
          setFilter={setFilter}
          showApplications={showApplications}
          placementCandidateId={placementCandidate.id}
        />
      )}

      {(placementCandidate.isRestricted ?? showApplications) ? undefined : (
        <Box
          sx={(theme) => ({
            position: "fixed",
            bottom: `calc(${theme.spacing(3)} + ${NAVBAR_HEIGHT}px)`,
            left: "50%",
            transform: "translateX(-50%)",
            width: "auto",
            maxWidth: "90%",

            ...getSafeAreaPaddings({
              bottom: true,
              fallbackBottom: theme.spacing(5),
            }),
          })}
        >
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              color="primary"
              sx={{ width: "max-content" }}
              onClick={() => {
                const newViewMode = isListViewMode ? "map" : "list";
                setViewMode(newViewMode);
                logEvent(USER_EVENTS.TAPPED_JOBS_VIEW_MODE, {
                  viewMode: newViewMode,
                  filter: { ...filter, distanceInMiles: filter?.distanceInMiles?.$lt },
                  showApplications,
                });
              }}
            >
              {isListViewMode ? "View Map" : "View List"}
            </Button>
          </Stack>
        </Box>
      )}
    </>
  );
}
